import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { AutoDrawRun } from '../../types';

const ACTIVE_STATUSES = new Set(['queued', 'processing']);

export interface StartAutoDrawResponse {
  run_id: number;
  status: string;
}

export interface AutoDrawConflict {
  run_id?: number;
  status?: string;
  requires_replace?: boolean;
  error?: string;
}

// Kick off an Auto-draw run. 409 payloads (active run / requires_replace)
// are surfaced on the thrown error as `conflict` for the caller to handle.
export function useStartAutoDraw() {
  return useMutation({
    mutationFn: async (params: { measurementId: number; replace?: boolean }) => {
      try {
        const response = await api.post(
          `/api/measurements/${params.measurementId}/auto-draw/`,
          params.replace ? { replace: true } : {}
        );
        return response.data as StartAutoDrawResponse;
      } catch (error: unknown) {
        // lib/api.ts throws Error objects with .status and .responseData attached
        const apiError = error as { status?: number; responseData?: AutoDrawConflict };
        if (apiError.status === 409 && apiError.responseData) {
          (error as { conflict?: AutoDrawConflict }).conflict = apiError.responseData;
        }
        throw error;
      }
    },
  });
}

// Poll the latest run while it is queued/processing. When the run reaches
// `completed`, the detected geometry landed as roof-sections/features/faces —
// invalidate those queries once so the map picks the new overlays up.
export function useAutoDrawStatus(measurementId?: number, enabled = true) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['auto-draw-status', measurementId],
    queryFn: async () => {
      try {
        const response = await api.get(`/api/measurements/${measurementId}/auto-draw-status/`);
        return response.data as AutoDrawRun;
      } catch (error: unknown) {
        if ((error as { status?: number }).status === 404) {
          return null; // no runs yet — not an error state
        }
        throw error;
      }
    },
    enabled: !!measurementId && enabled,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status && ACTIVE_STATUSES.has(status) ? 2000 : false;
    },
    retry: false,
  });

  const previousStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const status = query.data?.status;
    if (
      status === 'completed' &&
      previousStatusRef.current &&
      ACTIVE_STATUSES.has(previousStatusRef.current)
    ) {
      queryClient.invalidateQueries({ queryKey: ['roof-sections'] });
      queryClient.invalidateQueries({ queryKey: ['roof-features'] });
      queryClient.invalidateQueries({ queryKey: ['roof-faces'] });
      queryClient.invalidateQueries({ queryKey: ['roof-feature-summary'] });
    }
    previousStatusRef.current = status;
  }, [query.data?.status, queryClient]);

  return query;
}

export function useAcceptAutoDraw() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      measurementId: number;
      runId: number;
      predominantPitch?: string;
      wasteFactor?: number;
    }) => {
      const response = await api.post(
        `/api/measurements/${params.measurementId}/auto-draw-accept/`,
        {
          run_id: params.runId,
          predominant_pitch: params.predominantPitch,
          waste_factor: params.wasteFactor,
        }
      );
      return response.data as { id: number; [key: string]: unknown };
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['auto-draw-status', params.measurementId] });
      queryClient.invalidateQueries({ queryKey: ['detailed-measurements'] });
      queryClient.invalidateQueries({ queryKey: ['detailedMeasurements'] });
      queryClient.invalidateQueries({ queryKey: ['detailedMeasurementsByOpportunity'] });
    },
  });
}

export function useRejectAutoDraw() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { measurementId: number; runId: number }) => {
      const response = await api.post(
        `/api/measurements/${params.measurementId}/auto-draw-reject/`,
        { run_id: params.runId }
      );
      return response.data as { run_id: number; status: string };
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['auto-draw-status', params.measurementId] });
      queryClient.invalidateQueries({ queryKey: ['roof-sections'] });
      queryClient.invalidateQueries({ queryKey: ['roof-features'] });
      queryClient.invalidateQueries({ queryKey: ['roof-faces'] });
      queryClient.invalidateQueries({ queryKey: ['roof-feature-summary'] });
    },
  });
}
